import { CheckIcon, MinusIcon } from '@chakra-ui/icons'
import { Box, Circle, Flex, HStack, Icon, Text, Tooltip } from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { noOp } from '@/utils/helpers'

import getSeriesStreakItemId from '@/components/Series/SeriesStreakBar/internal/getSeriesStreakItemId'
import getSeriesStreakItemTooltip from '@/components/Series/SeriesStreakBar/internal/getSeriesStreakItemTooltip'
import scrollToSeriesStreakItem from '@/components/Series/SeriesStreakBar/internal/scrollToSeriesStreakItem'
import type { SeriesStreakItem } from '@/components/Series/SeriesStreakBar/internal/types'

export default function SeriesStreakItems({
  items,
  hasUpcomingSessions,
}: {
  items: SeriesStreakItem[]
  hasUpcomingSessions: boolean
}) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [showLeftFade, setShowLeftFade] = useState(false)

  // We center the scroll on the session that most recently ended
  const mostRecentSessionIndex = items.findLastIndex(item => item.hasEnded)
  const targetScrollIndex = Math.max(mostRecentSessionIndex, 0)

  // Scroll to the target session and show the left fade after a short delay
  useEffect(() => {
    if (targetScrollIndex >= 0) {
      scrollToSeriesStreakItem(targetScrollIndex)

      setTimeout(() => {
        if (scrollContainerRef.current != null) {
          setShowLeftFade(scrollContainerRef.current.scrollLeft > 0)
        }
      }, 300)
    }
  }, [targetScrollIndex, items])

  // Show the left fade once the user starts scrolling
  useEffect(() => {
    const container = scrollContainerRef.current

    if (container == null) {
      return noOp
    }

    const handleScroll = () => {
      setShowLeftFade(container.scrollLeft > 0)
    }

    handleScroll()

    container.addEventListener('scroll', handleScroll)

    return () => {
      container.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return (
    <Box maxWidth={{ base: '100%', md: hasUpcomingSessions ? '70%' : '100%' }}>
      <Box position="relative" overflow="hidden">
        <HStack
          spacing="12px"
          ref={scrollContainerRef}
          // From windsurf: to make the content fade out in the right in order to indicate signal that users can scroll
          css={{
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            '-ms-overflow-style': 'none',
            scrollbarWidth: 'none',
            overflowX: 'scroll',
            paddingRight: '32px',
            width: '100%',
            flexWrap: 'nowrap',
          }}
        >
          {items.map((item, index) => {
            const { month, day } = item.startDate
            const formattedDate = `${month}/${day}`

            return (
              <Flex
                key={`${item.startDate}`}
                id={getSeriesStreakItemId(index)}
                direction="column"
                alignItems="center"
                width="40px"
                flex="0 0 auto"
              >
                <Text fontSize="14px" fontWeight="medium" mb="8px" color="fg.muted">
                  {formattedDate}
                </Text>

                <Tooltip
                  label={getSeriesStreakItemTooltip(item)}
                  placement="bottom"
                  hasArrow
                  openDelay={300}
                >
                  <Circle
                    size="24px"
                    bg={
                      item.hasEnded
                        ? item.attended
                          ? 'success.solid'
                          : item.attendanceRequired
                            ? 'error.solid'
                            : 'gray.solid'
                        : 'bg.emphasized'
                    }
                    color={
                      item.hasEnded && !item.attended && !item.attendanceRequired
                        ? 'gray.contrast'
                        : 'white'
                    }
                  >
                    {item.hasEnded ? (
                      item.attended ? (
                        <Icon as={CheckIcon} boxSize="12px" />
                      ) : (
                        <Icon as={MinusIcon} boxSize="12px" />
                      )
                    ) : null}
                  </Circle>
                </Tooltip>
              </Flex>
            )
          })}
        </HStack>
        {/* Gradient overlay for right fading edge effect */}
        <Box
          position="absolute"
          right="0"
          top="0"
          bottom="0"
          width="40px"
          pointerEvents="none"
          background={`linear-gradient(to right, transparent, ${chakraTokenToVar('bg.subtle')})`}
        />

        {/* Gradient overlay for left fading edge effect - only shown when scrolled */}
        <Box
          position="absolute"
          left="0"
          top="0"
          bottom="0"
          width="40px"
          pointerEvents="none"
          background={`linear-gradient(to left, transparent, ${chakraTokenToVar('bg.subtle')})`}
          opacity={showLeftFade ? 1 : 0}
          transition="opacity 0.2s ease"
        />
      </Box>
    </Box>
  )
}
